// 보상 카드 뽑기 연출의 순수 규칙(DOM 없음): 시간표·카운트다운·자동 선택·카드 앞면 구성·파티클 상한.
// 화면은 ui/rewardCards.ts(카드)·ui/clearFx.ts(CLEAR 연출)가 이 값을 쓴다. 게임 로직(보상 굴림·지급)은 shared/content/cardReward.ts·game/flow.ts.
import { CARD_RARITY_COLOR, CARD_RARITY_NAME, cardTitle, parseCardLoot, type CardRarity } from '../shared/content/cardReward';
import { STONE_TIERS } from '../shared/content/economy';
import { gemIconPath, consIconPath, gearDefIconCandidates } from './itemIcons';

/** 연출 시간표(ms). 한곳에 모아 시험이 순서·합계를 고정한다 */
export const TIMELINE = {
  /** CLEAR 연출 */
  flashMs: 160,
  logoAtMs: 90,
  logoMs: 560,
  /** 별 3개가 차례로 쾅(첫 별 시각, 간격) */
  starFirstMs: 980,
  starGapMs: 360,
  starMs: 360,
  /** CLEAR 연출 끝(결과 표로 넘어가는 시각) */
  clearEndMs: 2400,
  /** 결과 표를 보여 주는 시간(이어받은 결과는 짧게) */
  tableMs: 1500,
  tableRestoredMs: 500,
  /** 카드가 날아와 깔리는 시간: 카드마다 시작 간격과 한 장 비행 시간 */
  flyGapMs: 170,
  flyMs: 640,
  /** 뒤집기·들어올리기 */
  liftMs: 280,
  flipMs: 560,
  /** 선택 카드가 뒤집힌 뒤 나머지 카드를 차례로 뒤집기 시작하기까지, 카드 사이 간격 */
  othersDelayMs: 900,
  othersGapMs: 260,
  /** 모든 카드가 공개된 뒤 자동으로 닫히기까지(눌러서 넘기면 바로) */
  closeAfterMs: 1700,
} as const;

/** 고르는 제한 시간(초). 0이 되면 자동 선택 */
export const PICK_SECONDS = 10;
/** 마지막 몇 초는 붉게 깜박인다 */
export const URGENT_SECONDS = 3;

/** 날아오는 시작 시각(카드 i) */
export function flyStartMs(i: number): number { return i * TIMELINE.flyGapMs; }
/** 모든 카드가 착지하는 시각 */
export function landedMs(count = 3): number { return flyStartMs(count - 1) + TIMELINE.flyMs; }
/** 선택한 카드가 공개를 마치는 시각(선택 시각 기준 상대) */
export function chosenRevealedMs(): number { return TIMELINE.liftMs + TIMELINE.flipMs; }
/** 나머지 카드 j번째(0..)가 뒤집히기 시작하는 시각(선택 시각 기준 상대) */
export function otherFlipMs(j: number): number { return TIMELINE.othersDelayMs + j * TIMELINE.othersGapMs; }
/** 모든 공개가 끝나는 시각(선택 시각 기준 상대, 카드 count장) */
export function allRevealedMs(count = 3): number { return otherFlipMs(count - 2) + TIMELINE.flipMs; }

/** 영수증 id로 정하는 자동 선택 카드(0..count-1). 같은 영수증은 같은 칸이라 새로고침해도 달라지지 않는다 */
export function autoPickIndex(resultId: string, count = 3): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < resultId.length; i++) { h ^= resultId.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) % count;
}

/**
 * 카운트다운: 화면이 가려진 시간·프레임 끊김은 세지 않도록 한 번에 최대 maxStepMs만 흐른다(탭이 숨겨졌다 돌아와도 바로 자동 선택되지 않는다).
 * 객체를 매 프레임 만들지 않는다(필드만 갱신).
 */
export class Countdown {
  /** 남은 ms. start 전에는 전체 */
  left = PICK_SECONDS * 1000;
  running = false;
  constructor(readonly totalMs = PICK_SECONDS * 1000, readonly maxStepMs = 250) { this.left = totalMs; }
  start(): void { this.running = true; this.left = this.totalMs; }
  stop(): void { this.running = false; }
  /** dtMs만큼 흐른다. 막 0이 됐으면 true(한 번만) */
  step(dtMs: number): boolean {
    if (!this.running) return false;
    this.left -= Math.max(0, Math.min(dtMs, this.maxStepMs));
    if (this.left <= 0) { this.left = 0; this.running = false; return true; }
    return false;
  }
  /** 표시용 남은 초(올림, 10..0) */
  get seconds(): number { return Math.ceil(this.left / 1000); }
  get urgent(): boolean { return this.running && this.seconds <= URGENT_SECONDS; }
  /** 0..1 남은 비율(게이지) */
  get ratio(): number { return this.left / this.totalMs; }
}

export interface CardFace {
  readonly rarity: CardRarity;
  readonly rarityName: string;
  readonly color: string;
  /** 종류 글자(골드·강화석·보석·소비·장비) */
  readonly label: string;
  readonly name: string;
  readonly qty: string;
  /** 그림 후보(앞에서부터 시도, assetUrl 전 경로). 없으면 빈 배열 */
  readonly icons: readonly string[];
  readonly kind: string;
}
const STONE_ICON = STONE_TIERS.map((s) => `ui/items/${s.id}.webp`);

/** 카드 한 장의 앞면 내용. 영수증 문자열이 없거나 틀리면 골드 카드 */
export function cardFaceOf(desc: string, gold: number, gearName: (id: string) => string): CardFace {
  const c = parseCardLoot(desc);
  const t = cardTitle(desc, gold, gearName);
  if (!c || c.kind === 'gold') {
    // 이전 영수증(문자열 없음)은 액수가 클수록 빛나지 않는다: 일반 등급 골드
    const r = (c ? c.rarity : 0) as CardRarity;
    return { rarity: r, rarityName: CARD_RARITY_NAME[r], color: CARD_RARITY_COLOR[r], label: t.label, name: t.name, qty: t.qty, icons: ['ui/rewards/gold-coins.webp'], kind: 'gold' };
  }
  let icons: string[] = [];
  if (c.kind === 'stone') icons = [STONE_ICON[c.code] ?? STONE_ICON[0]];
  else if (c.kind === 'gem') { const p = gemIconPath(c.code); icons = p ? [p] : []; }
  else if (c.kind === 'cons') { const p = consIconPath(c.id); icons = p ? [p] : []; }
  else icons = [...gearDefIconCandidates(c.id, c.grade), 'ui/items/cat_gear.webp'];
  return { rarity: c.rarity, rarityName: CARD_RARITY_NAME[c.rarity], color: CARD_RARITY_COLOR[c.rarity], label: t.label, name: t.name, qty: t.qty, icons, kind: c.kind };
}

/** 등급별 빛 연출: 전설만 화면 흔들림·광선, 영웅 이상 광선 약하게 */
export interface RarityFx { readonly glowPx: number; readonly rays: 0 | 1 | 2; readonly shake: boolean; readonly burst: number }
export const RARITY_FX: readonly RarityFx[] = [
  { glowPx: 10, rays: 0, shake: false, burst: 6 },
  { glowPx: 18, rays: 0, shake: false, burst: 12 },
  { glowPx: 28, rays: 1, shake: false, burst: 20 },
  { glowPx: 40, rays: 2, shake: true, burst: 34 },
];

/** 동시에 살아 있는 파티클 상한: 큰 화면 48, 폰 가로(짧은 변 ≤ 480) 24, 움직임 줄이기 설정이면 0 */
export function particleBudget(w: number, h: number, reducedMotion: boolean): number {
  if (reducedMotion) return 0;
  return Math.min(w, h) <= 480 ? 24 : 48;
}
